import React, { useState, useCallback, useMemo } from 'react';
import { Autocomplete, Loader, Group, Text, Badge } from '@mantine/core';
import { IconBuilding, IconSearch } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { BusinessSearchAutocompleteProps } from './BusinessSearchAutocomplete.types';
import { useSearchBusinessesQuery } from '../../../../application/queries/businessQueries';
import { BusinessSummary } from '../../../../domain/entities/Business';

/**
 * BusinessSearchAutocomplete Component
 *
 * Optimized predictive search autocomplete for finding businesses (10k+ records)
 * - Debounced search (500ms) for performance with large datasets
 * - Displays business name and region badge
 * - Used in registration flow
 *
 * Features:
 * - Real-time predictive search as user types
 * - Display business name with region name badge
 * - Loading indicator during search
 * - Minimum 2 characters to trigger search
 * - Returns top 5 most relevant results
 * - Optional region filtering
 * - "No results" message with guidance
 *
 * Usage:
 * ```tsx
 * <BusinessSearchAutocomplete
 *   value={selectedBusiness}
 *   onChange={handleBusinessSelect}
 *   label="Search for your business"
 *   required
 * />
 * ```
 */
export const BusinessSearchAutocomplete: React.FC<BusinessSearchAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Start typing to search...',
  label,
  description,
  error,
  required = false,
  disabled = false,
  regionId
}): React.ReactElement => {
  const [searchValue, setSearchValue] = useState<string>(value?.displayName ?? '');
  const [debouncedSearch] = useDebouncedValue(searchValue, 500);

  // Use React Query hook for business search
  // Only search when debounced value is at least 2 characters
  const { data: searchResults = [], isLoading } = useSearchBusinessesQuery(
    debouncedSearch.trim(),
    10,
    regionId,
    debouncedSearch.trim().length >= 2
  );

  // Filter businesses based on region if specified
  const filteredBusinesses = useMemo((): BusinessSummary[] => {
    if (regionId) {
      return searchResults.filter((b) => b.regionId === regionId);
    }
    return searchResults;
  }, [searchResults, regionId]);

  // Convert to autocomplete data format
  // Use ID as value (unique) and displayName + region as label to handle duplicates
  const autocompleteData = useMemo((): { value: string; label: string }[] => {
    return filteredBusinesses.map((b) => ({
      value: b.id,
      label: `${b.displayName} (${b.regionName})`
    }));
  }, [filteredBusinesses]);

  // Handle selection
  const handleSelect = useCallback(
    (selectedValue: string): void => {
      // Find the selected business by ID (the value is now the business ID)
      const selectedBusiness = filteredBusinesses.find((b) => b.id === selectedValue);
      if (selectedBusiness) {
        onChange(selectedBusiness);
        setSearchValue(selectedBusiness.displayName);
      }
    },
    [filteredBusinesses, onChange]
  );

  // Handle input change
  const handleChange = useCallback(
    (newValue: string): void => {
      setSearchValue(newValue);

      // Clear selection if input is cleared
      if (!newValue && value) {
        onChange(null);
      }
    },
    [value, onChange]
  );

  // Custom item render function to show business with category
  const renderOption = useCallback(
    ({ option }: { option: { value: string; label: string } }): React.ReactElement => {
      // option.value is now the business ID
      const business = filteredBusinesses.find((b) => b.id === option.value);

      if (!business) {
        return <Text>{option.label}</Text>;
      }

      return (
        <Group gap="xs" wrap="nowrap">
          <IconBuilding size={16} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>
              {business.displayName}
            </Text>
          </div>
          <Badge size="sm" variant="light" color="violet" style={{ flexShrink: 0 }}>
            {business.regionName}
          </Badge>
        </Group>
      );
    },
    [filteredBusinesses]
  );

  return (
    <Autocomplete
      label={label}
      description={description}
      placeholder={placeholder}
      value={searchValue}
      onChange={handleChange}
      onOptionSubmit={handleSelect}
      data={autocompleteData}
      error={error}
      required={required}
      disabled={disabled}
      leftSection={<IconSearch size={16} />}
      rightSection={isLoading ? <Loader size="xs" /> : null}
      limit={5}
      maxDropdownHeight={250}
      renderOption={renderOption}
    />
  );
};
